import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { AuthLayout, AuthLink } from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AUTH } from "@/constants/testIds";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(err.message || "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Access your AgentHQ workspace."
      footer={<>New to RESIDENNCE? <AuthLink to="/signup" testId={AUTH.toSignup}>Create an account</AuthLink></>}
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid={AUTH.signinPage} noValidate>
        {error && (
          <Alert variant="destructive" data-testid={AUTH.error}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} data-testid={AUTH.email} placeholder="you@example.com" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <AuthLink to="/forgot-password" testId={AUTH.toForgot}>Forgot?</AuthLink>
          </div>
          <Input id="password" type="password" autoComplete="current-password" required value={password}
            onChange={(e) => setPassword(e.target.value)} data-testid={AUTH.password} placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid={AUTH.submit}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
        </Button>
      </form>
    </AuthLayout>
  );
}
